/**
 * TeamDeactivatedScreen
 *
 * Shown when a team's subscription has been cancelled and the team is deactivated
 * (teams/{teamId}.isActive === false OR entitlement.isDeactivated === true).
 *
 * DISTINCT from AccessDisabledScreen (member-level access=false).
 * This is team-level deactivation — ALL members see this screen.
 *
 * Contract: Team Deactivation & Access Gating Contract V1.0 §3
 * Detection order: isDeactivated checked BEFORE isMemberDisabled.
 */

import { Building2, LogOut, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface TeamDeactivatedScreenProps {
  teamName?: string;
}

export function TeamDeactivatedScreen({ teamName }: TeamDeactivatedScreenProps) {
  const { t } = useTranslation('common');
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      window.location.href = '/login';
    }
  };

  const handleTeamPicker = () => {
    navigate('/team-picker', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center space-y-6 max-w-md w-full">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-semibold">
            {t('teamDeactivated.title', 'Team gedeactiveerd')}
          </h1>
          {teamName && <p className="text-sm font-medium text-muted-foreground">{teamName}</p>}
          <p className="text-sm text-muted-foreground">
            {t(
              'teamDeactivated.description',
              'Dit team is gedeactiveerd. Het abonnement is beëindigd. Neem contact op met support voor meer informatie.'
            )}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button onClick={handleTeamPicker}>
            <Users className="w-4 h-4 mr-2" />
            {t('teamDeactivated.switchTeam', 'Naar teamkeuze')}
          </Button>

          <Button variant="ghost" onClick={handleSignOut}>
            <LogOut className="w-4 h-4 mr-2" />
            {t('teamDeactivated.signOut', 'Uitloggen')}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('teamDeactivated.supportNote', 'support@modcar.io')}
        </p>
      </div>
    </div>
  );
}
